import { IMicroConfiguration, TViewDefinition } from '../types/configuration';
import { StateChange, TRegionChange } from '../transformations/state_change';
import { ResourceLoader } from '../builders/resource_loader';
import { RegionBuilder } from '../builders/region_builder';
import { TSubscription } from '../types/subscription';
import { ViewEmpty } from '../internal/view_empty';
import { ViewBuilder } from '../builders/view_builder';
import { ViewRenderProcess } from './view_render';
import { ViewModelProcess } from './view_model';
import { Micro } from '../core/micro';

import { IRegion, IRouteRequest, IRoute } from'../../../dip/library/di-hnf/di-hnf.js';
import { Ignition, Process, View } from'../../../pip/library/pi-interop/pi-interop.js';
import navigation, { TParameters } from'../../../pip/library/pi-navigation/pi-navigation.js';
import ess from'../../../pip/library/pi-ess/pi-ess.js';
import f1 from'../../../pip/library/pi-f1/pi-f1.js';

export class ViewStateProcess extends Process {
  id = 'view_state';

  dependencies = ['/view_model', '/view_render'];
  processes: {
    view_render: ViewRenderProcess;
    view_model: ViewModelProcess;
  };

  transformations: {
    stateChange: StateChange;
  };

  configuration: IMicroConfiguration;

  readonly ignition: Ignition = new Ignition();
  private __activeRegions: Map<string, View> = new Map();
  private __micro: Micro<IMicroConfiguration>;

  private __activeState: IRoute | undefined;

  set active(state: IRoute) {
    this.__activeState = state;
  }

  get active() {
    return {
      regions: [],
      ...(this.__activeState || {})
    } as IRoute;
  }

  constructor(micro: Micro<IMicroConfiguration>) {
    super();
    this.__micro = micro;
  }

  /**
   *
   * @param configuration
   */
  initialize(configuration: IMicroConfiguration) {
    this.configuration = configuration;
    this.__activeRegions.clear();

    this.__activeState = undefined;
    this.transformations = {
      stateChange: new StateChange()
    };

    return f1.done(this);
  }

  uninitialize() {
    this.__activeRegions.clear();
    return f1.done(this);
  }

  transitionTo(stateRequest: IRouteRequest) {
    const stateRoute = this.__mapRequestToState(stateRequest);
    if (!stateRoute) {
      return f1.done(f1.skip);
    }

    const activeState = this.active;
    const parentParameters = f1.reduce(
      (params: TParameters, _stateRoute) => {
        return { ...params, ...(_stateRoute.parameters || {}) };
      },
      {},
      stateRoute.parents || []
    ) as TParameters;

    const queryParams = navigation.uri.readParameters(window.location.hash);
    const parameters = {
      ...parentParameters,
      ...(stateRoute.parameters || {}),
      ...(stateRequest.parameters || {}),
      ...(activeState.parameters || {}),
      ...(queryParams || {})
    };

    const regionChange = this.transformations.stateChange.deriveRegionChange(stateRoute, activeState);
    const stateSubscriptionChange = this.transformations.stateChange.deriveStateSubscriptions(stateRoute, activeState);
    const regionSubscriptionChange = this.transformations.stateChange.deriveRegionSubscriptions(regionChange);
    const micro = this.__micro;
    const site = 'micro';

    return f1
      .serial(
        [
          () => ess.tap(micro, { site, action: 'transition-start', data: { regionChange, stateRoute, parameters } }),
          () => ess.tap(micro, { site, action: 'notify-state-exit', data: stateSubscriptionChange.exit }),
          () => this.__notifySubscriptions(stateSubscriptionChange.exit, parameters),
          () => ess.tap(micro, { site, action: 'notify-region-exit', data: regionSubscriptionChange.exit }),
          () => this.__notifySubscriptions(regionSubscriptionChange.exit, parameters),
          () => ess.tap(micro, { site, action: 'unload-regions', data: regionChange }),
          () => this.__unloadRegions(regionChange),
          () => ess.tap(micro, { site, action: 'reflect-location', data: parameters }),
          () => this.__reflectLocation(parameters),
          () => ess.tap(micro, { site, action: 'notify-state-change', data: { regionChange, stateRoute, parameters } }),
          () => this.__notifyStateChange(),
          () => ess.tap(micro, { site, action: 'notify-state-enter', data: stateSubscriptionChange.enter }),
          () => this.__notifySubscriptions(stateSubscriptionChange.enter, parameters),
          () => ess.tap(micro, { site, action: 'notify-region-enter', data: stateSubscriptionChange.enter }),
          () => this.__notifySubscriptions(regionSubscriptionChange.enter, parameters),
          () => ess.tap(micro, { site, action: 'load-regions', data: { regionChange, stateRoute, parameters } }),
          () => this.__loadRegions(regionChange, stateRoute, parameters),
          () => ess.tap(micro, { site, action: 'notify-state-update', data: parameters }),
          () => this.__notifySubscriptions(stateSubscriptionChange.update, parameters),
          () => ess.tap(micro, { site, action: 'notify-region-update', data: parameters }),
          () => this.__notifySubscriptions(regionSubscriptionChange.update, parameters),
          () => ess.tap(micro, { site, action: 'notify-state-query', data: parameters }),
          () => this.__notifySubscriptions(stateSubscriptionChange.query, parameters),
          () => ess.tap(micro, { site, action: 'notify-region-query', data: parameters }),
          () => this.__notifySubscriptions(regionSubscriptionChange.query, parameters),
          () => ess.tap(micro, { site, action: 'reflect-style-rules', data: stateRoute }),
          () => this.__reflectStyleRules(stateRoute),
          () => ess.tap(micro, { site, action: 'transition-end', data: { regionChange, stateRoute, parameters } })
        ],
        []
      )
      .then(() => (this.active = stateRoute));
  }

  private __mapRequestToState(stateRequest: IRouteRequest): IRoute {
    const stateTable = this.processes.view_model.models.routeTable;
    const stateKey = stateRequest.state;

    // @todo what if it does not exist?
    return stateTable[stateKey] as IRoute;
  }

  /**
   *
   */
  private __loadRegions(regionChange: TRegionChange, stateRoute: IRoute, parameters: TParameters) {
    return f1.parallel(
      f1.map((region) => {
        // @note creates a ITask, replace with f1.to.task
        return () =>
          f1.tasks(
            [
              (region: IRegion) => {
                // 1. load view remotely or locally
                return ResourceLoader.loadView(region, this.processes.view_model.models.preloadedViews);
              },
              (ViewJS: TViewDefinition) => {
                // 2. create the view
                return ViewBuilder.build(ViewJS, this.configuration.view);
              },
              (view: View) => {
                // 3. create region and view
                return f1.done({
                  // @question how do I target an existing region and not create one?
                  regionEl: this.processes.view_render.locateRegion(region),
                  view
                });
              },
              ({ regionEl, view }: { regionEl: HTMLElement; view: View }) => {
                // 4. startup the view
                const configuration = {
                  ...this.__micro.configuration,
                  routes: {
                    uri: stateRoute.abs_uri,
                    parameters
                  }
                };

                return this.ignition.startup(view, configuration).then(() => ({
                  regionEl,
                  view
                }));
              },
              ({ regionEl, view }: { regionEl: HTMLElement; view: View }) => {
                // 5. mount the view in to the region
                return this.ignition.mount(view, regionEl);
              },
              (viewJS: View) => {
                // 6. store the region internally to mark it as active
                return f1.done(this.__activeRegions.set(region.region, viewJS));
              }
            ],
            region
          );
      }, regionChange.load),
      f1.skip
    );
  }

  /**
   *
   */
  private __unloadRegions(regionChange: TRegionChange) {
    return f1.parallel(
      f1.map((region) => {
        return () =>
          f1.tasks(
            [
              (region: IRegion) => {
                // 1. find view in active region map
                const view: View | undefined = this.__activeRegions.get(region.region);
                this.__activeRegions.delete(region.region);

                // @todo what if the view cannot be found?
                return f1.done(view || new ViewEmpty());
              },
              (view: View) => {
                // 2. unmount the view
                return this.ignition.unmount(view);
              },
              (view: View) => {
                // 3. shutdown the view
                return this.ignition.shutdown(view);
              },
              () => {
                // 4. erase view region
                return f1.done(RegionBuilder.destroy(region, this.processes.view_render.root));
              }
            ],
            region
          );
      }, regionChange.unload),
      f1.skip
    );
  }

  private async __reflectLocation(parameters: TParameters): Promise<unknown> {
    navigation.location.update(parameters);
    return this;
  }

  private async __notifyStateChange(): Promise<unknown> {
    // notify all subordinate Views
    //
    const activeRegions = this.__activeRegions.values();
    for await (const view of activeRegions) {
      if (view.stateChange) {
        view.stateChange();
      }
    }

    // notify the parent micro (also a View)
    //
    await this.__micro.stateChange();
    return this;
  }

  /**
   *
   */
  private async __reflectStyleRules(activeState: IRoute): Promise<unknown> {
    return this.processes.view_render.reflectStyleRules(activeState);
  }

  private __notifySubscriptions(subscribers: string[], parameters: TParameters) {
    const micro = this.__micro;
    const cbs = f1.compact(
      f1.reduce(
        (list, subscriber) => {
          return f1.push(micro.subscriber(subscriber), list);
        },
        [],
        subscribers
      ) as TSubscription[]
    );

    return Promise.all(f1.map((s) => s(parameters) || Promise.resolve(), cbs));
  }
}
